import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowRight, AlertTriangle, Sparkles, Shield, Ban, Link, Search } from 'lucide-react';

const ProductTheftLienCheck: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation />

      {/* Main Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative">
          <div className="max-w-6xl mx-auto px-8 pt-32 pb-16">
            <div className="text-center">
              <div className="inline-flex items-center space-x-3 px-4 py-2.5 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-8">
                <AlertTriangle className="w-4 h-4 text-teal-400" />
                <span className="text-sm font-semibold text-slate-300">Risk Protection</span>
              </div>

              <h1 className="text-6xl lg:text-7xl font-black leading-tight tracking-tighter mb-8">
                <span className="block">Theft & Lien</span>
                <span className="block" style={{color: '#AD0000'}}>
                  Check
                </span>
              </h1>

              <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto mb-12">
                Protect your business from stolen vehicles and undisclosed liens.
                Real-time checks against national databases ensure you're making safe transactions.
              </p>

              <button
                onClick={() => navigate('/register')}
                className="group px-8 py-4 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 text-white font-bold rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg flex items-center space-x-3 mx-auto"
              >
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-250" />
              </button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="relative py-16 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-8">
            <h2 className="text-4xl font-black mb-12 text-center">
              Complete <span className="text-white">Protection</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {[
                {
                  icon: Ban,
                  title: 'Theft Records',
                  description: 'Real-time checks against NICB and law enforcement databases for stolen vehicle alerts.',
                  gradient: 'from-red-500 to-orange-600'
                },
                {
                  icon: Link,
                  title: 'Lien Status',
                  description: 'Verify outstanding liens and encumbrances before completing any transaction.',
                  gradient: 'from-teal-500 to-cyan-600'
                },
              ].map((feature, i) => (
                <div key={i} className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all">
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-black mb-3 text-white">{feature.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative py-16">
          <div className="max-w-7xl mx-auto px-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10">
              <h2 className="text-3xl font-black mb-8 text-center">
                Why <span className="text-teal-400">Risk Protection</span> Matters
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Search, label: 'Instant Results', value: '<2s' },
                  { icon: Shield, label: 'Accuracy Rate', value: '99.9%' },
                  { icon: AlertTriangle, label: 'Risk Flags', value: '15+' },
                  { icon: Sparkles, label: 'Coverage', value: 'National' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-6 bg-white/5 rounded-xl">
                    <stat.icon className="w-8 h-8 text-teal-400 mx-auto mb-3" />
                    <div className="text-3xl font-black text-white mb-1">{stat.value}</div>
                    <div className="text-sm text-slate-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-32">
          <div className="max-w-4xl mx-auto px-8 text-center">
            <h3 className="text-5xl font-black mb-6">Protect Your Business</h3>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
              Don't risk buying stolen vehicles or those with hidden liens. Get protected today.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="px-10 py-5 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 text-white font-bold text-lg rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg"
            >
              Create Your Account
            </button>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ProductTheftLienCheck;
